// ================================================================
// Bootstrap — راه‌اندازی اپ
// ================================================================

import './styles/reset.css';
import './styles/variables.css';
import './styles/base.css';
import './styles/bidi.css';
// Geister-Zeiger des Assistenten — global, weil er über JEDER Oberfläche liegt
import './core/ghostPointer.css';

import { initI18n, t } from './i18n/index.js';
import { state } from './core/state.js';
import { storage } from './core/storage.js';
import { ensureAuthenticated, clearSession } from './core/auth.js';
import { startSessionWatch, stopSessionWatch } from './core/sessionWatch.js';
import { loadOrgTheme } from './core/orgTheme.js';
import { installDigitNormalizer } from './utils/digits.js';
import { showAuthGate } from './components/auth-gate/AuthGate.js';
import { showInviteAccept } from './components/auth-gate/InviteAccept.js';

import { events, EVENTS } from './core/events.js';
import { renderSidebar } from './components/sidebar/Sidebar.js';
import { renderCompanySidebar } from './components/sidebar/CompanySidebar.js';
import { renderCompaniesOverview, refreshOverview } from './components/companies-overview/CompaniesOverview.js';
import { initScenarioStore } from './core/scenarioStore.js';
import { renderTopbar } from './components/topbar/Topbar.js';
import { renderCanvas } from './components/canvas/Canvas.js';
import { renderBottombar } from './components/bottombar/Bottombar.js';
import { initModals } from './components/modals/index.js';
import { initContextMenu } from './components/context-menu/ContextMenu.js';
import { getUser, needsOnboarding, completeOnboarding, fetchMe } from './core/auth.js';
import { showCompanyDashboard } from './components/company-dashboard/CompanyDashboard.js';
import { installMfaSetupPrompt } from './components/mfa/MfaSetupDialog.js';
import { isMobileDevice, showLeadMobile } from './components/lead-mobile/leadMobile.js';


// ساختار اولیه DOM
function renderShell() {
  const app = document.getElementById('app');
  app.innerHTML = `
    <aside class="sidebar" id="sidebar"></aside>
    <aside class="sidebar-secondary" id="sidebar-secondary"></aside>
    <main class="main" id="main" data-view="overview">
      <div class="overview-wrap" id="overview-wrap"></div>
      <header class="topbar" id="topbar"></header>
      <div class="canvas-wrap" id="canvas-wrap"></div>
      <footer class="bottombar" id="bottombar"></footer>
    </main>
    <div id="modal-root"></div>
  `;
}

// راه‌اندازی خودِ اپ — فقط بعد از اینکه احراز هویت تأیید شد
function startApp() {
  // عنوان صفحه
  document.title = t('app.title');

  // Plattform-Konten ohne zweiten Faktor: 403 MFA_SETUP_REQUIRED öffnet die Einrichtung.
  installMfaSetupPrompt();

  // state اولیه — اگه چیزی در localStorage هست بازخوانی کن
  const saved = storage.load();
  if (saved) {
    state.nodes = saved.nodes || [];
    state.connections = saved.connections || [];
    state.nextId = saved.nextId || 1;
  } else {
    // node اولیه: Start
    state.addNode('start', 280, 240);
  }

  // shell + component‌ها
  renderShell();
  renderSidebar(document.getElementById('sidebar'));
  renderCompanySidebar(document.getElementById('sidebar-secondary'));
  renderCompaniesOverview(document.getElementById('overview-wrap'));
  initScenarioStore();
  renderTopbar(document.getElementById('topbar'));
  renderCanvas(document.getElementById('canvas-wrap'));
  renderBottombar(document.getElementById('bottombar'));
  initModals(document.getElementById('modal-root'));
  initContextMenu(document.getElementById('modal-root'));

  // باز شدنِ یک شرکت → نمای canvas؛ دکمه‌ی back در topbar → نمای کلی
  events.on(EVENTS.SCENARIO_LOADED, () => setMainView('canvas'));
  events.on(EVENTS.SHOW_OVERVIEW, () => { setMainView('overview'); refreshOverview(); });

  // فرود اولیه: نمای کلیِ شرکت‌ها
  setMainView('overview');
}

// جابه‌جایی بین نمای کلی و فضای کاریِ canvas
function setMainView(view) {
  const main = document.getElementById('main');
  if (main) main.dataset.view = view;
}

async function bootstrap() {
  // 0) Ziffern vereinheitlichen — VOR allem anderen, damit auch die Eingaben
  // auf dem Login-Schirm schon lateinische Ziffern liefern. Auf persischer
  // Tastatur getippte «۱۲۳» sind für JavaScript keine Zahl (parseFloat → NaN)
  // und für einen \D-Filter gar nichts (Telefonnummer wird leer).
  installDigitNormalizer();

  // 1) زبان رو راه‌اندازی کن (gate هم بهش نیاز داره)
  // Standard: Deutsch. Das Produkt richtet sich an Schweizer Handwerksbetriebe —
  // wer noch nie umgeschaltet hat, soll Deutsch sehen. Eine getroffene Wahl liegt
  // in localStorage und gewinnt (siehe initI18n).
  await initI18n('de');
  document.title = t('app.title');

  // مسیر دعوت — عمومی، فارغ از وضعیت لاگین
  const inviteMatch = window.location.pathname.match(/^\/invite\/(.+)$/);
  if (inviteMatch) {
    showInviteAccept(document.getElementById('app'), decodeURIComponent(inviteMatch[1]));
    return;
  }

  // «Passwort vergessen»-Link aus der E-Mail — ebenfalls öffentlich. Bewusst VOR
  // dem Auth-Check: wer noch eine alte Session hat, soll das Passwort trotzdem
  // neu setzen können (und wird dabei überall abgemeldet).
  const resetMatch = window.location.pathname.match(/^\/reset-password\/(.+)$/);
  if (resetMatch) {
    const { showResetPassword } = await import('./components/auth-gate/ResetPassword.js');
    showResetPassword(document.getElementById('app'), decodeURIComponent(resetMatch[1]));
    return;
  }

  // Öffentliche Dokument-Seite: /offerte/<token> und /rechnung/<token>.
  // Der KUNDE sieht sein Dokument, lädt die PDF und nimmt (bei Offerten) an.
  // Muss VOR dem Auth-Gate stehen: hier gibt es kein Konto, der Token im Link
  // ist die ganze Berechtigung. Zwei Pfade, EINE Seite — die Adresse verrät
  // dem Kunden schon vor dem Öffnen, worum es geht.
  const docMatch = window.location.pathname.match(/^\/(offerte|rechnung)\/(.+)$/);
  if (docMatch) {
    const { showPublicOffer } = await import('./components/public-offer/PublicOffer.js');
    showPublicOffer(document.getElementById('app'), decodeURIComponent(docMatch[2]));
    return;
  }

  // DEV-Vorschau des Onboarding-Wizards (?onboarding=1) — reiner Design-Preview
  // ohne Auth/Backend. Wird durch den echten Trigger (erster Login neuer Org)
  // ersetzt, sobald der Flow steht.
  if (new URLSearchParams(window.location.search).get('onboarding') === '1') {
    const { showOnboarding } = await import('./react/components/onboarding/showOnboarding.ts');
    showOnboarding(document.getElementById('app'), {
      orgName: 'CleanAndMove',
      preview: true,
      // «Einrichtung abschließen» im Preview → zurück auf die Startseite
      // (ohne ?onboarding, sonst ginge es sofort wieder in den Wizard).
      onDone: () => { window.location.href = '/'; },
    });
    return;
  }

  // 1.5) Rückkehr von der Stripe-Bezahlseite.
  // Stripe schickt den Kunden mit ?checkout=success zurück. Der Webhook ist
  // die verlässliche Quelle, braucht aber Sekunden — ohne diesen aktiven
  // Abgleich landete der Kunde auf der Sperr-Seite, obwohl er GERADE bezahlt
  // hat. Danach die Parameter aus der Adresse werfen: ein Neuladen (oder ein
  // geteilter Link) soll den Abgleich nicht wiederholen.
  const params = new URLSearchParams(window.location.search);
  const checkout = params.get('checkout');
  if (checkout) {
    if (checkout === 'success') {
      try {
        const { syncAfterCheckout } = await import('./core/subscription.js');
        await syncAfterCheckout(params.get('session_id'));
        // Der Nutzer im Speicher trägt noch das alte Abo — frisch holen,
        // sonst zeigte die App gleich wieder die Bezahlschranke.
        const { fetchMe } = await import('./core/auth.js');
        await fetchMe().catch(() => {});
      } catch { /* der Webhook zieht den Stand ohnehin nach */ }
    }
    window.history.replaceState({}, '', window.location.pathname);
  }

  // «Ein Konto, ein Gerät»: wird diese Sitzung von einem anderen Gerät
  // beendet, muss die App SOFORT zurück an die Anmeldemaske — egal ob das
  // Signal live über den Ereignisstrom kam (sessionWatch) oder eine beliebige
  // Anfrage den 401 einfing (apiClient). Beide Wege enden auf diesem Ereignis,
  // und der Grund liegt bereits im Speicher (core/signedOut.js), damit die
  // Anmeldemaske ihn zeigen kann.
  //
  // `once`: nach dem ersten Rauswurf steht ohnehin die Anmeldemaske; ein
  // zweites Signal aus einer noch laufenden Anfrage würde sie nur neu bauen
  // und dabei die eben getippte E-Mail wegwerfen.
  window.addEventListener('lw:session-revoked', onRevoked, { once: true });

  // 2) گاردِ احراز هویت: اگه نشست معتبره اپ بالا بیاد، وگرنه gate
  //
  // ensureAuthenticated() ruft /api/auth/me — das IST zugleich die Frage
  // «bin ich noch angemeldet?». Wurde dieses Gerät in der Zwischenzeit
  // rausgeworfen, kommt der 401 mit Grund zurück, und die App zeigt die
  // Anmeldemaske MIT Erklärung, statt kommentarlos den alten Bildschirm.
 const authed = await ensureAuthenticated();
  if (authed) {
    routeAfterAuth();
  } else {
    showAuthGate(document.getElementById('app'), routeAfterAuth);
  }
}

// Der eine Ausgang für «diese Sitzung ist beendet» — gleich, woher das Signal
// kam. Der GRUND liegt schon in core/signedOut.js; die Anmeldemaske liest ihn
// beim Öffnen aus.
function onRevoked() {
  stopSessionWatch();
  clearSession();
  showAuthGate(document.getElementById('app'), routeAfterAuth);
}

// Zurück aus dem Hintergrund? Dann fragen, ob die Sitzung noch lebt.
//
// Das ist der Fall, für den die Live-Wache NICHT reicht: liegt das Handy in
// der Tasche, friert iOS den Ereignisstrom ein — der Rauswurf erreicht es nie.
// Ohne diese Prüfung sähe der Nutzer beim Aufwecken zuerst den alten, längst
// ungültigen Bildschirm und erführe erst bei seiner nächsten Aktion, dass er
// abgemeldet ist. Die Antwort kommt über den üblichen Weg: /api/auth/me
// liefert bei entzogener Sitzung den 401 mit Grund, und apiClient löst damit
// dasselbe Ereignis aus wie überall sonst.
let resumeBound = false;
function watchAppResume() {
  if (resumeBound) return;
  resumeBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    import('./core/auth.js').then(({ fetchMe, getToken }) => {
      // Ohne Token ist die App ohnehin in der Anmeldemaske.
      if (!getToken()) return;
      // Fehler absichtlich geschluckt: ein Rauswurf meldet sich über das
      // Ereignis, alles andere (Funkloch, Serverpause) darf niemanden
      // abmelden.
      fetchMe().catch(() => {});
    });
  });
}

// Nach dem Login: der Einrichtungs-Assistent erscheint GENAU EINMAL pro Firma —
// solange organization.onboardingCompletedAt leer ist. Danach (und für alle
// Bestandskunden, die die Migration auf «erledigt» gesetzt hat) geht es direkt
// in die App. Der Zeitstempel liegt am Server, nicht im Browser: sonst käme der
// Assistent auf jedem neuen Gerät desselben Kunden wieder.
function routeAfterAuth() {
  const app = document.getElementById('app');
  // Von hier führt JEDER Weg in die angemeldete App — der Boot mit gültiger
  // Sitzung ebenso wie der frische Login. Darum hängt die Sitzungs-Wache hier
  // und nicht am Boot allein: sonst liefe sie nach einem Login erst ab dem
  // nächsten Neuladen.
  startSessionWatch(onRevoked);
  watchAppResume();
  // Laufzeit abgelaufen → Abo-Seite statt App. VOR dem Assistenten: wessen
  // Zugang abgelaufen ist, soll nicht erst durch die Einrichtung geschickt
  // werden. Der Server hat das gerechnet (organization.subscription), der
  // Browser rechnet hier nichts nach — und er sperrt auch nicht selbst:
  // die API antwortet ohne laufendes Abo mit 402.
  const sub = getUser()?.organization?.subscription;
  if (sub?.expired) {
    // Eine noch hängende Hinweis-Leiste über der Sperre wäre doppelt gesagt.
    import('./react/components/plans/showSubscriptionNotice.ts')
      .then(({ hideSubscriptionNotice }) => hideSubscriptionNotice()).catch(() => {});
    showSubscriptionGate(app, sub);
    return;
  }
  // Handy-Orgs (leadMobileUi, z.B. aus der Selbst-Registrierung) überspringen
  // den Desktop-Assistenten: er ist eine Desktop-Oberfläche, die mobile App
  // hat ihre eigenen Einstellungen. Direkt in die App routen.
  const u = getUser();
  if (!needsOnboarding() || u?.organization?.leadMobileUi === true) { routeToApp(); return; }
  import('./react/components/onboarding/showOnboarding.ts').then(({ showOnboarding }) => {
    showOnboarding(app, {
      orgName: u?.organization?.name || 'Workspace',
      // Erst merken, DANN weiterrouten. Schlägt das Merken fehl (Netz weg),
      // kommt der Nutzer trotzdem in die App — der Assistent erscheint dann
      // beim nächsten Login noch einmal, statt ihn hier festzuhalten.
      onDone: () => { completeOnboarding().catch(() => {}).then(routeToApp); },
    });
  }).catch(() => {
    // Chunk nicht ladbar (typisch: Tab von VOR einem Deploy, alter Hash weg)
    // → lieber ohne Assistent in die App als ewig auf «bitte warten» stehen.
    // Beim nächsten frischen Laden kommt der Assistent regulär wieder.
    routeToApp();
  });
}

// Abo abgelaufen: die Auswahl-Seite als Vollbild, ohne App dahinter. Nach der
// Wahl wird der Nutzer NEU geladen (fetchMe) — die neue Laufzeit kommt vom
// Server, nicht aus einer Annahme im Browser.
function showSubscriptionGate(app, state) {
  import('./react/components/plans/showSubscriptionGate.ts').then(({ showSubscriptionGate: show }) => {
    show(app, {
      state,
      onUnlocked: () => {
        fetchMe()
          .catch(() => {})
          .then(() => {
            import('./react/components/plans/showSubscriptionGate.ts')
              .then(({ hideSubscriptionGate }) => { hideSubscriptionGate(app); routeAfterAuth(); });
          });
      },
    });
  });
}

// مسیریابیِ عادیِ بعد از لاگین: مدیر پلتفرم → اپ کامل؛ بقیه → داشبورد شرکت.
// استثنا: سازمان‌هایی که leadMobileUi روشن دارند (سوییچ پلتفرم):
//   موبایل → رابط موبایلیِ Lead؛ دسکتاپ → صفحهٔ «این اپ برای موبایل است».
// این orgها فقط رابط موبایلی دارند، پس داشبورد کامل برایشان غلط است.
function routeToApp() {
  const app = document.getElementById('app');
  // Merken, mit welcher Antwort geroutet wurde — der resize-Wächter unten
  // vergleicht dagegen. MUSS hier passieren, nicht beim ersten resize: sonst
  // verpufft genau die erste Grössenänderung.
  lastWasMobile = isMobileDevice();
  // Firmenfarbe an <html> hängen, BEVOR eine Oberfläche zeichnet: die gemerkte
  // Farbe greift sofort, der Server-Wert korrigiert sie danach still.
  // Gilt für alle Oberflächen — die Farbe gehört der Firma, nicht dem Gerät.
  loadOrgTheme();
  // Vorwarnung «noch N Tage», bevor die Laufzeit endet — EINE Leiste über
  // beiden Oberflächen. Lazy und ohne .catch-Blockade: eine fehlende Leiste
  // darf das Routing nie aufhalten (der Chunk kann nach einem Deploy weg sein).
  import('./react/components/plans/showSubscriptionNotice.ts')
    .then(({ showSubscriptionNotice }) => {
      showSubscriptionNotice(getUser()?.organization?.subscription, () => {
        // Verlängert: neuen Zustand vom Server holen und neu routen — die
        // Leiste verschwindet dann von selbst, weil daysLeft wieder gross ist.
        fetchMe().catch(() => {}).then(routeAfterAuth);
      });
    })
    .catch(() => {});
  // React-Root des Onboardings sauber abbauen, bevor Vanilla das #app übernimmt.
  // Der Import darf das Routing NIE blockieren: schlägt er fehl (typisch: Tab
  // von vor einem Deploy, alter Chunk-Hash existiert nicht mehr), gibt es auch
  // keinen Onboarding-Root zum Abbauen — einfach weiterrouten. Vorher fror
  // genau hier der Login auf «bitte warten» ein, bis jemand neu lud.
  import('./react/components/onboarding/showOnboarding.ts')
    .then(({ hideOnboarding }) => hideOnboarding(app))
    .catch(() => {})
    .then(() => {
      const u = getUser();
      if (u?.organization?.isPlatform) {
        startApp();
      } else if (u?.organization?.leadMobileUi === true) {
        if (isMobileDevice()) showLeadMobile(app);
        // Nur-Mobil-Org am Rechner: die App im Handy-Rahmen (iframe) statt
        // des früheren «fürs Handy»-Hinweises — so ist sie auch am Desktop
        // benutzbar (u.a. für den Google-OAuth-Reviewer). Lazy geladen —
        // auf dem Handy soll dieser Code nie im Bundle-Pfad liegen.
        else import('./components/lead-mobile/PhoneFrame.js')
          .then(({ showPhoneFrame }) => showPhoneFrame(app));
      } else {
        showCompanyDashboard(app);
      }
    });
}

// Fenster von schmal auf breit (oder zurück) — z.B. Tablet gedreht oder ein
// Desktop-Fenster verkleinert: die Weiche oben hat dann die falsche Seite
// gewählt. Beim WECHSEL der Antwort einmal neu routen; ohne diesen Nachzug
// sitzt man im Hinweis, obwohl das Fenster längst Handy-Breite hat.
// Nur für Nur-Mobil-Orgs, und entprellt (resize feuert im Dutzend).
let lastWasMobile = null;
let routeResizeTimer = null;
window.addEventListener('resize', () => {
  const u = getUser();
  if (u?.organization?.leadMobileUi !== true || u?.organization?.isPlatform) return;
  if (lastWasMobile === null) return; // noch nicht geroutet — nichts zu prüfen
  if (isMobileDevice() === lastWasMobile) return;
  clearTimeout(routeResizeTimer);
  // routeToApp setzt lastWasMobile selbst neu
  routeResizeTimer = setTimeout(routeToApp, 250);
});

bootstrap();
